import React, { useEffect, useState } from "react"
import useLoader from "../../../../shared/components/loading-dialog/useLoader.hook";
import useReportingAndAnalyticsApi from "../../../pages/reporting-and-analytics/useReportingAndAnalyticsApi";
import LoadingDialogComponent from "../../../../shared/components/loading-dialog/loading-dialog.component";
import useWizardReportDataContext from "../contexts/useWizardReportData.context";
import { useWizardStepperContext } from "../contexts/useWizardStepper.context";
import { IWizardData } from "../contracts/IWizardData";
import { IWizardDetailsPostBody } from "../contracts/IWizardDetailsPostBody";
import { IReportPivotSelection } from "../../../../shared/contracts/IPivotSelection";



/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export function useSaveReportDetailsApi() {

    const { finishTrigger } = useWizardStepperContext();

    const { isNewReport, wizardData, availableColumns, availableDataSources, pivotData } = useWizardReportDataContext();
    const { saveWizardReportDetails } = useReportingAndAnalyticsApi();
    const [postBody, setPostBody] = useState<IWizardDetailsPostBody | null>(null);

    const transformPivotData = (): IReportPivotSelection[] => {
        const result: IReportPivotSelection[] = [];
        if (pivotData) {
            const parentCol = pivotData.parentColumn;
            const parentGroupBy = pivotData.parentGroupByFunction;
            // parent row
            result.push({
                columnId: parentCol?.columnId!,
                columnName: parentCol?.columnName!,
                columnDescription: parentCol?.columnDescription || '',
                columnDataType: parentCol?.columnDataType || '',
                formatString: parentCol?.formatString || '',
                aggregateFunction: parentGroupBy?.optionValue || '',
                orderType: parentCol?.orderType || '',
                groupBy: parentCol?.groupBy || '',
            })

            // child rows
            pivotData.childColumns?.forEach(child => {
                const childCol = availableColumns.find(x => x.columnName === child.childColumnName)!;
                result.push({
                    columnId: childCol.columnId,
                    columnName: childCol.columnName,
                    columnDataType: childCol.columnDataType || '',
                    orderType: childCol?.orderType || '',
                    groupBy: childCol?.groupBy || '',
                    // format
                    columnDescription: child.childColumnDescription || '',
                    formatString: child.childColumnFormatValue || '',
                    aggregateFunction: child.childColumnGroupByValue || '',
                })
            })
        }

        // ---
        return result;
    }

    useEffect(() => {
        if (finishTrigger > 0 && wizardData) {

            const dsViewName = availableDataSources.find(
                x => x.dataSourceCode === wizardData.datasourceGuid
            )?.dataSourceViewName.trim();

            setPostBody({
                reportId: wizardData.reportId,
                reportCode: wizardData.reportCode,
                reportName: wizardData.reportName,
                reportDescription: wizardData.reportDescription || '',
                reportDataSourceName: dsViewName || '',
                owner: wizardData.owner,
                permissions: wizardData.permissions,
                category: wizardData.category,
                bucket: wizardData.bucket,
                emailRecipients: wizardData.emailRecipients || '',
                exportData: wizardData.exportData || '',
                scheduleFrequency: wizardData.scheduleFrequency || '',
                scheduleDate: wizardData.scheduleDate
                ? new Date(wizardData.scheduleDate).toISOString()
                : '',
                reportSpecification: {
                    fetchDataWithExistingMetadata: wizardData.reportSpecification.fetchDataWithExistingMetadata,
                    filterCriterias: wizardData.reportSpecification.filterCriterias,
                    columnSelections: wizardData.reportSpecification.columnSelections,
                    pivotSelections: transformPivotData(),
                    styleInformation: wizardData.reportSpecification.styleInformation,
                    miscInformation: wizardData.reportSpecification.miscInformation,
                }
            })
        } else {
            setPostBody(null)
        }
    }, [finishTrigger])

    useEffect(() => {
        if (postBody) {
            saveReportDetailsLoader.loadAsync();
        }
    }, [postBody])



    const saveReportDetailsLoader = useLoader({
        loaderName: isNewReport ? "Create Report" : "Update Report",
        loadAsync: () =>
            saveWizardReportDetails(
                postBody!, //POST BODY
                {
                    doIf: () => saveReportDetailsLoader.isNotLoading,
                    doBefore: () => {
                        saveReportDetailsLoader.SET_LOADING(isNewReport ? "Creating Report..." : "Saving Updates...");
                    },
                    onFulfilled: (data: IWizardData) => {
                        saveReportDetailsLoader.SET_SUCCESS("Success.!");
                    },
                    onException: saveReportDetailsLoader.SET_EXCEPTION,
                }
            ),
    });


    const ReportDetailsSavingIndicator = () => {
        return <LoadingDialogComponent loaderHook={saveReportDetailsLoader} />
    }

    return {
        saveReportDetailsLoader,
        ReportDetailsSavingIndicator
    }
}